import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import '../styles/components/EmailVerification.css';

const API_URL = process.env.REACT_APP_API_URL;

// inline: true(기본) → 인풋/버튼 표시, false → 모달 기능만 사용
function EmailVerification(
  { formData, setFormData, emailVerified, setEmailVerified, onVerified, inline = true },
  ref
) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => { setIsModalOpen(false); setEmailMessage(''); setEmailCode(''); };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim());

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // 인증번호 요청 (코드 미반환)
  const requestEmailVerification = async () => {
    openModal(); // 즉시 모달 오픈
    const email = String(formData.email || '').trim();

    if (!validateEmail(email)) {
      setEmailMessage('올바른 이메일 형식을 입력해주세요.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/influencer/register/send-email-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.ok) {
        setEmailMessage('');
        setEmailCode(''); // 재요청 시 이전 입력 초기화
      } else {
        setEmailMessage(data.message || '이메일 인증 요청 실패');
      }
    } catch (err) {
      setEmailMessage('서버 요청 실패');
    }
    setLoading(false);
  };

  // 인증 코드 서버 검증
  const verifyEmailCode = async () => {
    const email = String(formData.email || '').trim();
    const input = String(emailCode || '').trim();

    if (!validateEmail(email)) { setEmailMessage('올바른 이메일 형식을 입력해주세요.'); return; }
    if (!input) { setEmailMessage('인증 코드를 입력해주세요.'); return; }

    setVerifying(true);
    try {
      const res = await fetch(`${API_URL}/api/influencer/register/verify-email-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: input })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setEmailVerified(true);
        setEmailMessage('');
      } else {
        setEmailMessage(data.message || '인증 코드가 올바르지 않습니다.');
      }
    } catch {
      setEmailMessage('서버 요청 실패');
    }
    setVerifying(false);
  };

  // 인증 완료 시 모달 닫고 상위 콜백
  useEffect(() => {
    if (emailVerified) {
      closeModal();
    }
  }, [emailVerified]);

  useEffect(() => {
    if (emailVerified && typeof onVerified === 'function') onVerified();
  }, [emailVerified, onVerified]);

  useImperativeHandle(ref, () => ({
    open: () => openModal(),
    openAndRequest: async () => { await requestEmailVerification(); },
    request: async () => { await requestEmailVerification(); }
  }));

  return (
    <div>
      {inline && (
        <div className="email-verification__input-group">
          <input
            className="email-verification__input"
            name="email"
            placeholder="이메일"
            value={formData.email}
            onChange={handleChange}
            required
            disabled={emailVerified || loading}
          />
          <button
            className="email-verification__button"
            type="button"
            onClick={requestEmailVerification}
            disabled={loading || emailVerified}
          >
            {emailVerified ? '인증 완료' : '이메일 인증 요청'}
          </button>
        </div>
      )}

      {isModalOpen && (
        <div className="email-verification-modal">
          <div className="email-verification-modal__content" onClick={(e) => e.stopPropagation()}>
            {validateEmail(formData.email) && (
              <>
                <p className="email-verification-modal__message">인증번호가 이메일로 전송되었습니다.</p>
                <p className="email-verification-modal__desc">아래 칸에 인증번호를 입력해주세요.</p>
                <input
                  className="email-verification-modal__input"
                  name="emailCode"
                  placeholder="인증 코드 입력"
                  value={emailCode}
                  onChange={(e) => setEmailCode(e.target.value)}
                  required
                />
                <div className="email-verification-modal__buttons">
                  <button
                    className="email-verification-modal__button email-verification-modal__button--primary"
                    type="button"
                    onClick={verifyEmailCode}
                    disabled={verifying}
                  >
                    {verifying ? '확인 중...' : '인증 코드 확인'}
                  </button>
                  <button
                    className="email-verification-modal__button"
                    type="button"
                    onClick={closeModal}
                  >
                    닫기
                  </button>
                </div>
              </>
            )}
            {emailMessage && <p className="email-verification-modal__error">{emailMessage}</p>}
            {!validateEmail(formData.email) && (
              <div className="email-verification-modal__buttons">
                <button
                  className="email-verification-modal__button"
                  type="button"
                  onClick={closeModal}
                >
                  닫기
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default forwardRef(EmailVerification);